import { ChangeDetectionStrategy, Component, ErrorHandler, Injector, Input, OnInit, ViewChild, forwardRef, inject, signal, untracked } from '@angular/core'
import { NG_VALUE_ACCESSOR, ValidatorFn } from '@angular/forms'
import { FieldTree, FormField, createMetadataKey, disabled, form } from '@angular/forms/signals'

import { MatInput } from '@angular/material/input'

import { ValueAccessorBase } from '../../../_jaettu-angular/_components/value-accessor-base'
import { arvonMuutokset, asetaArvo } from '../../_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../../_lomakkeet/reaktiivinen-validaattori'
import { LaskunTuoteTypeaheadBase } from '../../service/lasku/typeahead.service'

import { AsiakkaanTuote, TypeaheadTuote } from '@lemon/core-firebase/model/lasku'

import { BehaviorSubject } from 'rxjs'
import { FormValidationService } from '../../../_jaettu-angular/service/form-validation.service'

/** Sidotun kentän validaattori, jolla tarkistetaan myös hakukenttään kirjoitettu teksti. */
export const TUOTEHAUN_VALIDAATTORI = createMetadataKey<ValidatorFn>()

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => TuoteHakuComponent),
  multi: true,
}

@Component({
  selector: 'app-tuote-haku',
  template: `
    <mat-form-field>
      <input
        #inputti
        autocomplete="kirjanpitoaineistoarkki"
        [formField]="haku"
        [placeholder]="'tuote.aloita-kirjoittamalla-tuotteen-nimi' | translate | async"
        [attr.aria-label]="ariaLabel"
        [matAutocomplete]="tuoteamaattinen"
        (blur)="onBlur()"
        matInput
        data-lpignore="true"
      />
      <mat-error>
        <ng-content></ng-content>
      </mat-error>
    </mat-form-field>

    <mat-autocomplete #tuoteamaattinen [displayWith]="getDisplayFn()">
      @for (tuote of suodatetutTuotteet | async; track tuote) {
        <mat-option [value]="tuote">
          {{ tuote.nimi }}
        </mat-option>
      }
    </mat-autocomplete>
  `,
  providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TuoteHakuComponent extends ValueAccessorBase<AsiakkaanTuote> implements OnInit {
  @ViewChild('inputti', { read: MatInput, static: true }) materialInput: MatInput

  @Input() paivitaValidaatiot: any
  @Input() ariaLabel: string

  private readonly _injector = inject(Injector)
  private _kentta: FieldTree<AsiakkaanTuote>
  private readonly _poissa = signal(false)
  private readonly _validaattori = signal<ValidatorFn | null>(null)
  _disabled: boolean = false

  haku = form(signal<TypeaheadTuote | AsiakkaanTuote | string | null>(null), polku => {
    disabled(polku, () => this._poissa())
    validoiReaktiivisesti(polku, control => this._validaattori()?.(control) ?? null)
  })
  suodatetutTuotteet: BehaviorSubject<TypeaheadTuote[]> = new BehaviorSubject([])

  constructor(
    private laskunTuoteTypeahead: LaskunTuoteTypeaheadBase,
    private validationService: FormValidationService,
    private errorHandler: ErrorHandler,
  ) {
    super()
  }

  ngOnInit() {
    // Kenttä luetaan vasta nyt, koska FormField-direktiivi tarvitsee luodessaan tämän komponentin arvonvälittäjänä
    this._kentta = this._injector.get(FormField).field() as FieldTree<AsiakkaanTuote>
    const kentanTila = this._kentta()

    this._poissa.set(untracked(() => kentanTila.disabled()))
    this._validaattori.set(untracked(() => kentanTila.metadata(TUOTEHAUN_VALIDAATTORI)?.()) ?? null)
    asetaArvo(
      this.haku,
      untracked(() => kentanTila.value()),
      { ilmoita: false },
    )

    this.registerOnWrite(value => this._kirjoitaHakuun(value))
    // FormField kirjoittaa alkuarvon jo ennen ngOnInitia, jolloin kirjoituksen kuuntelija puuttui vielä
    if (this.value !== undefined) {
      this._kirjoitaHakuun(this.value)
    }

    arvonMuutokset(this.haku, { injector: this._injector }).subscribe((value: TypeaheadTuote) => {
      if (!value) {
        this.suodatetutTuotteet.next([])
        this.value = null
        return
      }

      if (typeof value === 'string') {
        const kasitelty = this.validationService.processValue(value)
        // console.log('kasitelty', kasitelty)
        this.laskunTuoteTypeahead.haeJaSuodata(kasitelty).then(
          result => {
            if (result.loytynyt == null) {
              const tuote: AsiakkaanTuote = {
                $key: null,
                nimi: kasitelty,
                hinta: null,
                date: null,
                alv: null,
              }
              this.value = tuote
              this.suodatetutTuotteet.next(result.suodatetut)
            } else {
              this.laskunTuoteTypeahead
                .getTuote(result.loytynyt.avain)
                .then(tuote => {
                  this.value = tuote
                  this.suodatetutTuotteet.next(result.suodatetut)
                })
                .catch(err => {
                  this.errorHandler.handleError(err)
                })
            }
          },
          err => {
            this.errorHandler.handleError(err)
          },
        )
      } else {
        const avain = value.avain as string
        if (avain && avain.replace(/\s/g, '') !== '') {
          // console.log('Hae tuotetta', value)
          this.laskunTuoteTypeahead
            .getTuote(avain)
            .then(tuote => {
              if (tuote) {
                // console.log('löytynyt tuote', tuote)
                this.value = tuote
                this.suodatetutTuotteet.next([])
              } else {
                // console.log('ei löytynyt tuotetta')
                const t: AsiakkaanTuote = {
                  $key: avain,
                  nimi: value.nimi,
                  hinta: null,
                  date: null,
                  alv: null,
                }
                this.value = t
                this.suodatetutTuotteet.next([])
              }
            })
            .catch(err => {
              this.errorHandler.handleError(err)
            })
        }
      }
    })
  }

  onBlur() {
    this.touch()
  }

  public focus() {
    this.materialInput.focus()
  }

  markAsTouched() {
    this._kentta().markAsTouched()
    this.haku().markAsTouched()
    this.touch()
  }

  getDisplayFn() {
    return val => this.display(val)
  }

  private display(tuote: AsiakkaanTuote): string {
    if (tuote && tuote.nimi) {
      return tuote.nimi
    }
    return ''
  }

  private _kirjoitaHakuun(value: AsiakkaanTuote) {
    asetaArvo(
      this.haku,
      {
        avain: value.$key,
        nimi: value.nimi,
      },
      { ilmoita: false },
    )
    // JOS TÄMÄ ON PÄÄLLÄ, niin CONTROLLILLE TULEVAT ARVOT LAUKAISEVAT MYÖS ULOSPÄIN ARVON VIENNIN!!
    // TÄMÄ AIHEUTTAA ONGELMAN KUN MUOKATAAN LASKULUONNOSTA, JA TYPEAHEAD TUOTTEELLA ON ERI HINTA KUIN LASKURIVILLÄ
    // (LUONNOKSEN TALLENTAMINEN EI TALLENNA HINTAA TUOTTEESEEN)

    // EN TIEDÄ; MIKSI TÄMÄ ON JOSKUS PÄÄLLE LAITETTU???

    // this.ctrl.updateValueAndValidity()
  }
}
